// When the help centre may show its Photohawk app card (owner, 29 Oct 2026): "If we're to directly offer or push a
// way to download photos via the Photohawk app then it should probably only happen for non Pro/Max users as many want
// the white-labelled experience."
//
// So the card NEVER appears in the context of a Pro or Max workspace (plan ids pro, max and the legacy enterprise:
// canonicalPlan), only for Lite/free workspaces' buyers (free; legacy payg / lite) and in photohawk.com's own context.
// A help page doesn't know which gallery sent the visitor, so the context is, in order:
//
//   1. ?ws=<alias>  a gallery (or an order email) links to /help/…?ws=<alias>: that workspace.
//   2. the referrer   <alias>.photohawk.com → that workspace; photohawk.com itself, search engines and social sites →
//                     no workspace; any other site → it may be a workspace's custom domain (Pro/Max only), so it's
//                     looked up too.
//   3. otherwise      no workspace: photohawk.com-branded help, the card is shown.
//
// With a workspace, the card waits for its plan from the public tenant record (GET {search}/albums?hostname=, the
// same anonymous endpoint the public pages use; `plan` is on its allowlist, lib/publicRecords). It shows only if the
// plan is free; Pro, Max, an unknown answer or a failed lookup keep it hidden (fail closed: a white-label workspace
// must never see it). The server renders the same decision from ?ws= and the Referer header, so a Pro/Max visitor
// never sees the card flash.

import { canonicalPlan } from "@/lib/plans/entitlements";

export type PromoContext =
  | { kind: "none" }
  | { kind: "workspace"; hostname: string; via: "ws" | "referrer" };

/** The site's own domains: a subdomain of one is a workspace alias unless it's reserved. */
const BASE_DOMAINS = ["photohawk.com", "photohawk-staging.com"];
/** Subdomains that are Photohawk itself, not a workspace (src/lib/hostTenant.ts's list, plus docs: the old help centre). */
const RESERVED = new Set([
  "www", "admin", "app", "api", "auth", "search", "workspaces", "checkout", "cdn", "assets", "static", "media", "img",
  "images", "mail", "email", "blog", "help", "docs", "support", "status", "go", "link", "dev", "staging", "upload",
]);
/** Referrers that are never a workspace (search engines, social and chat sites): no lookup for them. */
const NEVER_A_WORKSPACE = /(^|\.)(google\.[a-z.]+|bing\.com|duckduckgo\.com|yahoo\.[a-z.]+|ecosia\.org|baidu\.com|yandex\.[a-z.]+|naver\.com|brave\.com|facebook\.com|fb\.com|instagram\.com|t\.co|x\.com|twitter\.com|linkedin\.com|lnkd\.in|reddit\.com|whatsapp\.com|youtube\.com|tiktok\.com|chatgpt\.com|openai\.com|perplexity\.ai|claude\.ai|intercom\.help|intercom\.io|localhost)$/;

const ALIAS = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/** The workspace host for an alias on this deployment's domain ("acme" → "acme.photohawk.com"). */
export const aliasHost = (alias: string, apex: string) => `${alias}.${apex}`;

/**
 * The context a help page is read in.
 * @param search   the page's query string ("?ws=acme")
 * @param referrer the referring URL (document.referrer / the Referer header), "" if none
 * @param apex     this deployment's marketing apex (photohawk.com or photohawk-staging.com)
 */
export function promoContext(search: string, referrer: string, apex: string): PromoContext {
  const ws = new URLSearchParams(search).get("ws")?.trim().toLowerCase();
  if (ws && ALIAS.test(ws) && !RESERVED.has(ws)) return { kind: "workspace", hostname: aliasHost(ws, apex), via: "ws" };
  if (!referrer) return { kind: "none" };
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase().replace(/\.$/, "");
  } catch {
    return { kind: "none" };
  }
  if (!host || NEVER_A_WORKSPACE.test(host)) return { kind: "none" };
  for (const base of BASE_DOMAINS) {
    if (host === base) return { kind: "none" };
    if (host.endsWith(`.${base}`)) {
      const label = host.slice(0, -base.length - 1);
      // Only a single label is an alias (a.b.photohawk.com isn't a workspace).
      if (label.includes(".") || RESERVED.has(label) || !ALIAS.test(label)) return { kind: "none" };
      return { kind: "workspace", hostname: host, via: "referrer" };
    }
  }
  // Any other site: possibly a Pro/Max workspace's custom domain.
  return { kind: "workspace", hostname: host.replace(/^www\./, ""), via: "referrer" };
}

/** The stored plan ids that are Lite (canonical id free; legacy payg / lite). canonicalPlan maps an UNKNOWN id to free
 *  too, so the card checks this explicit list instead: a plan it doesn't recognise keeps the card hidden. */
const LITE_PLAN_IDS = new Set(["free", "payg", "pay", "pay-as-you-go", "payasyougo", "lite"]);

/** Whether a workspace on this plan may see the app card: Lite/free only (pro, max, enterprise or unknown never). */
export const planAllowsAppPromo = (plan: string | null | undefined): boolean => {
  const id = String(plan ?? "").trim().toLowerCase();
  return LITE_PLAN_IDS.has(id) && canonicalPlan(id) === "free";
};

/** The plan a public tenant record carries (entitlements.plan wins over the stored plan id). */
export const tenantPlan = (tenant: unknown): string | null => {
  if (!tenant || typeof tenant !== "object") return null;
  const t = tenant as { plan?: unknown; entitlements?: { plan?: unknown } };
  const p = t.entitlements?.plan ?? t.plan;
  return typeof p === "string" && p ? p : null;
};

export type PromoState = "show" | "hide" | "pending";

/** The card's state before any lookup: shown with no workspace, pending (hidden) until the plan is known otherwise. */
export const initialPromoState = (ctx: PromoContext): PromoState => (ctx.kind === "none" ? "show" : "pending");

/** The card's state once the workspace lookup answered (`tenant` undefined = the lookup failed). */
export const resolvedPromoState = (tenant: unknown): PromoState => (planAllowsAppPromo(tenantPlan(tenant)) ? "show" : "hide");
